import { InfiniteData, QueryKey, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { SPACE_FOLDER_PAGE_SIZE } from 'shared';

import { useLocale } from '../../../hooks/use-locale.hook';
import { StringKey } from '../../../lang/string-key.const';
import { SPACES_STALE_TIME_MS } from '../constants/spaces-query.const';
import { SpaceBrowserPagination } from '../types/data-room.types';

// What every page of the browser table answers with: folders before files, and the count of all.
type BrowserPage<File, Folder> = { files: File[]; folders: Folder[]; totalCount: number };

type Props<File, Folder> = {
  enabled: boolean;
  // Told once when a later page fails; with nothing shown the page says so itself.
  failureMessage: StringKey;
  // The caller owns the route and the company scope, so both stay where the request is built.
  fetchPage: (skip: number) => Promise<BrowserPage<File, Folder>>;
  // Whether the rows of the query shown before may stand in while this one loads: the same rows
  // in another order or for another term, never another folder's.
  keepsRowsOf: (previousKey: QueryKey) => boolean;
  queryKey: QueryKey;
};

// `never[]` takes the place of any row type, so one empty list serves every page shape.
const NO_ROWS: never[] = [];

const countLoaded = (pages: BrowserPage<unknown, unknown>[]): number =>
  pages.reduce((count, page) => count + page.folders.length + page.files.length, 0);

// Back to the first page, as the cap table's "Show less" does; the rest is fetched again on demand.
const toFirstPage = <Page>(
  data: InfiniteData<Page, number> | undefined,
): InfiniteData<Page, number> | undefined =>
  data && { pageParams: data.pageParams.slice(0, 1), pages: data.pages.slice(0, 1) };

/**
 * The browser table's rows 50 at a time, sub-folders first; each "Load more" asks for the next 50
 * and "Show less" folds back to the first. A folder's contents and a space's search results both
 * page this way and differ only in what they ask the server.
 */
export const useBrowserPages = <File, Folder>({
  enabled,
  failureMessage,
  fetchPage,
  keepsRowsOf,
  queryKey,
}: Props<File, Folder>) => {
  const queryClient = useQueryClient();
  const { messagesLocale } = useLocale();
  // When this query became the one shown; a failure older than that is the cache's.
  const { at: shownSince } = useMemo(() => ({ at: Date.now(), queryKey }), [queryKey]);

  const {
    data,
    error,
    errorUpdatedAt,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isPending,
    isPlaceholderData,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    enabled,
    getNextPageParam: (lastPage, pages) => {
      const loaded = countLoaded(pages);

      return loaded < lastPage.totalCount ? loaded : undefined;
    },
    initialPageParam: 0,
    placeholderData: (previous, previousQuery) =>
      previousQuery && keepsRowsOf(previousQuery.queryKey) ? previous : undefined,
    staleTime: SPACES_STALE_TIME_MS,
  });

  // With nothing shown the page says so itself; a failed later page or refresh keeps the rows on
  // screen, so it is told once. A failure the cache kept from an earlier visit was told then, so
  // coming back does not tell it again.
  const hasRows = !!data?.pages.length;

  useEffect(() => {
    if (error && hasRows && errorUpdatedAt >= shownSince) {
      toast.error(messagesLocale[failureMessage]);
    }
  }, [error, errorUpdatedAt, failureMessage, hasRows, messagesLocale, shownSince]);

  // One list across the pages, rebuilt only when a page arrives.
  const rows = useMemo(() => {
    const pages = data?.pages;

    if (!pages?.length) return null;

    return {
      files: pages.flatMap((page) => page.files),
      folders: pages.flatMap((page) => page.folders),
      pageCount: pages.length,
      remainingCount: Math.max(0, pages[pages.length - 1].totalCount - countLoaded(pages)),
    };
  }, [data]);

  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  const showLess = useCallback(
    () =>
      queryClient.setQueryData<InfiniteData<BrowserPage<File, Folder>, number>>(
        queryKey,
        toFirstPage,
      ),
    [queryClient, queryKey],
  );

  const retry = useCallback(() => void refetch(), [refetch]);

  // The stand-in pages are another query's, so none of them is continued or folded; the bar keeps
  // the sizes it showed for those very rows, only disabled, so it does not drop out and jump back.
  const isSettled = !isPlaceholderData;
  const settledSizes = useRef({ canShowLess: false, nextPageSize: 0, remainingCount: 0 });

  if (isSettled) {
    const remaining = rows?.remainingCount ?? 0;

    settledSizes.current = {
      canShowLess: !hasNextPage && (rows?.pageCount ?? 0) > 1,
      nextPageSize: hasNextPage ? Math.min(SPACE_FOLDER_PAGE_SIZE, remaining) : 0,
      remainingCount: remaining,
    };
  }

  const { canShowLess, nextPageSize, remainingCount } = settledSizes.current;

  // One object for the table, stable while nothing about the pages changes.
  const pagination = useMemo(
    (): SpaceBrowserPagination => ({
      canShowLess,
      isLoadingMore: isFetchingNextPage,
      isReloading: isPlaceholderData,
      loadMore,
      nextPageSize,
      remainingCount,
      showLess,
    }),
    [
      canShowLess,
      isFetchingNextPage,
      isPlaceholderData,
      loadMore,
      nextPageSize,
      remainingCount,
      showLess,
    ],
  );

  return {
    files: rows?.files ?? NO_ROWS,
    folders: rows?.folders ?? NO_ROWS,
    // The first page failed: there is nothing to show, not an empty folder.
    isError: enabled && isError && !hasRows,
    isPending: enabled && isPending,
    pagination,
    retry,
  };
};
